<script setup lang="ts">
import { computed } from 'vue'
import dayjs from '@/lib/dayjs'
import weekday from 'dayjs/plugin/weekday'
import EventCell from './EventCell.vue'
import type { WebinarCalendarEvent, CalendarEmitEvent } from '../types'

const props = defineProps<{
  viewDate: Date
  currentDate: Date
  events: WebinarCalendarEvent[]
}>()

const emit = defineEmits<{
  (e: 'event-click', event: CalendarEmitEvent): void
}>()

dayjs.extend(weekday)

const weekdayInfo = computed(() => {
  return {
    label: dayjs(props.viewDate).format('dddd'),
    number: dayjs(props.viewDate).format('D'),
    isCurrent: dayjs(props.viewDate).isSame(props.currentDate, 'day')
  }
})

const hourLabels = computed(() => {
  const result: string[] = []
  for (let i = 0; i < 24; i++) {
    result.push(dayjs().hour(i).format('HH:00'))
  }
  return result
})

const hours = computed(() => {
  const periodStart = dayjs(props.viewDate).startOf('day')
  const periodEnd = dayjs(props.viewDate).endOf('day')
  const result: Array<{ value: Date; isPast: boolean; isCurrent: boolean }> = []

  let hour = dayjs(periodStart).startOf('hour')
  while (hour.isBefore(periodEnd)) {
    result.push({
      value: hour.toDate(),
      isPast: hour.isBefore(props.currentDate, 'hour'),
      isCurrent: hour.isSame(props.currentDate, 'hour')
    })
    hour = hour.add(1, 'hour')
  }

  return result
})

const eventCells = computed(() => {
  return props.events.map(event => {
    const rowStart = dayjs(event.start_time).hour() + 1
    const rowEnd = dayjs(event.end_time).hour() + 1
    return { ...event, _style: { 'grid-row-start': rowStart, 'grid-row-end': rowEnd } }
  })
})

function capitalize(value: string) {
  if (!value) return ''
  return value.charAt(0).toUpperCase() + value.slice(1)
}
</script>

<template>
  <div class="webinar-calendar__view webinar-calendar__view_week">
    <div class="webinar-calendar__day-header">
      <div class="webinar-calendar__weekday">GMT +03:00</div>

      <div class="webinar-calendar__weekday" :class="{ 'webinar-calendar__weekday_active': weekdayInfo.isCurrent }">
        <span>{{ capitalize(weekdayInfo.label) }}</span>
        <span class="webinar-calendar__weekday-number" v-text="weekdayInfo.number" />
      </div>
    </div>

    <div class="webinar-calendar__day-container">
      <div class="webinar-calendar__day-labels-grid">
        <div v-for="(label, i) in hourLabels" :key="i" class="webinar-calendar__hour-label">
          {{ capitalize(label) }}
        </div>
      </div>

      <div class="webinar-calendar__day-main-grid">
        <div class="webinar-calendar__day-background-grid">
          <div
            v-for="hour in hours"
            :key="hour.value.toJSON()"
            class="webinar-calendar__hour-cell"
            :class="{ 'webinar-calendar__hour-cell_past': hour.isPast }"
          >
            <svg v-if="hour.isCurrent" class="webinar-calendar__hour-cell-track">
              <line x1="0" y1="5" x2="100%" y2="5" stroke-width="2" />
              <circle cx="4.5" cy="5" r="4.5" />
            </svg>
          </div>
        </div>

        <div class="webinar-calendar__day-events-grid">
          <EventCell
            v-for="event in eventCells"
            :key="event.id"
            class="webinar-calendar__day-event-cell"
            :event="event"
            :style="event._style"
            @event-click="e => emit('event-click', e)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
@use 'sass:color';

.webinar-calendar {
  &__view_week {
    display: flex;
    flex-direction: column;
    overflow-x: auto;
  }

  // Header
  &__day-header {
    display: grid;
    grid-template-columns: 56px 1fr;
  }

  &__weekday {
    flex: 1 0 auto;
    padding: 8px 8px 6px;

    display: flex;
    flex-direction: column;

    @include text-caption;

    border: 1px $lightGray solid;
    &:not(:last-child) {
      border-right: none;
    }

    &:first-child {
      justify-content: flex-end;
      text-align: center;
    }

    &_active {
      padding: 6px 8px 6px;
      border-top: 2px $blue solid;
      color: $blue;
    }
  }

  &__weekday-number {
    @include text-body-bold;
    margin-top: 2px;
  }

  // Content
  &__day-container {
    flex: 1;
    margin: -1px 0 0;

    display: grid;
    grid-template-columns: 56px 1fr;
    border-bottom: 1px $lightGray solid;
  }

  // Labels (at the left)
  &__day-labels-grid {
    display: grid;
    grid-template-rows: repeat(24, 70px);
  }

  &__hour-label {
    @include text-caption;
    color: $darkGray;
    border-left: 1px $lightGray solid;

    display: flex;
    justify-content: center;
    align-items: center;
  }

  // Values (main events grid)
  &__day-main-grid {
    position: relative;
  }

  &__day-events-grid,
  &__day-background-grid {
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;

    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: repeat(24, 70px);
  }

  &__day-events-grid {
    z-index: 1;
  }

  &__hour-cell {
    border: 1px color.adjust($lightGray, $alpha: -0.4) solid;
    margin: 0 -1px -1px 0;

    padding: 4px;

    display: flex;
    flex-direction: column;
    position: relative;

    &_disabled {
      color: $gray;
    }

    &_today {
      background: $lightenBlue;
      border-top: 3px $blue solid;
      color: $blue;
    }

    &_past {
      background: $lightenGray;
    }
  }

  &__hour-cell-track {
    width: calc(100% + 8px);
    height: 10px;
    position: absolute;
    top: -6px;
    left: -4px;
    z-index: 2;

    fill: $red;
    stroke: $red;
  }

  &__day-event-cell {
    height: 100%;
  }
}
</style>
